import cx from "clsx";
import {
  GalleryArtwork,
  SecondaryListingEntry,
} from "kreate-common/modules/business-types";
import ButtonSecondary from "kreate-common/modules/kreate-ui/components/ButtonSecondary";
import Flex from "kreate-common/modules/kreate-ui/components/Flex";
import { safeParseKreateUtxoSecondary } from "kreate-common/modules/utxo-parser/by-type/secondary";
import React from "react";
import { MdRemoveShoppingCart, MdShoppingCart } from "react-icons/md";

import InputNumEditions$Compact from "../../../../../../../PageArtwork/containers/ArtworkDetailsViewer/components/InputNumEditions$Compact";
import { runFlow$DelistAsset } from "../../../../generators/runFlow$DelistAsset";
import { runFlow$PurchaseAsset } from "../../../../generators/runFlow$PurchaseAsset";
import Definition from "../../../../modals/ModalListAsset/components/Definition";

import { SellerNameViewer } from "./components/SellerNameViewer";
import styles from "./index.module.scss";

import { useCardanoWalletApi } from "@/modules/cardano-wallet";
import { useBusyCount } from "@/modules/common-hooks/useBusyCount";
import {
  formatInteger$Compact,
  formatInteger$Standard,
  formatLovelaceAmount$Compact,
  formatLovelaceAmount$Standard,
} from "@/modules/common-utils/format";
import { InlineError } from "@/modules/form-field/errors";
import { useField$Natural } from "@/modules/form-field/hooks/useField$Natural";
import InlineErrorViewer from "@/modules/kreate-components/components/InlineErrorViewer";
import { useModalTrigger$ModalAlert } from "@/modules/kreate-modals/modals/ModalAlert/hooks";
import { useModalTrigger$ModalAsyncGenerator } from "@/modules/kreate-modals/modals/ModalAsyncGenerator/hooks";
import { useModalTrigger$ModalConfirm } from "@/modules/kreate-modals/modals/ModalConfirm/hooks";
import { useModalTrigger$ModalConnectWallet } from "@/modules/kreate-modals/modals/ModalConnectWallet/hooks/useModalTrigger$ModalConnectWallet";
import { useModalTrigger$ModalFailure } from "@/modules/kreate-modals/modals/ModalFailure/hooks";

type Props = {
  className?: string;
  style?: React.CSSProperties;
  galleryArtwork: GalleryArtwork;
  entry: SecondaryListingEntry;
};

export default function ListingEntryViewer({
  className,
  style,
  galleryArtwork,
  entry,
}: Props) {
  const wallet = useCardanoWalletApi();
  const parsedUtxo = safeParseKreateUtxoSecondary(entry.utxo).result;
  const fieldNumEditions = useField$Natural({
    min: 1,
    max: parsedUtxo?.listedAsset.quantity ?? 1e6,
    initialValue: 1,
  });

  const modalTriggers = {
    ModalAsyncGenerator: useModalTrigger$ModalAsyncGenerator(),
    ModalFailure: useModalTrigger$ModalFailure(),
    ModalConnectWallet: useModalTrigger$ModalConnectWallet(),
    ModalAlert: useModalTrigger$ModalAlert(),
    ModalConfirm: useModalTrigger$ModalConfirm(),
  };

  const handleError = async (error: unknown) => {
    if (error instanceof InlineError) return;
    await modalTriggers.ModalFailure.openModalWithError(error);
  };

  const { busy, withCustomErrorHandler } = useBusyCount();

  const isConnectedToSellerWallet =
    wallet.type === "ready" &&
    wallet.address === parsedUtxo?.datumSecondary.sellerAddress;

  const handlePurchase = withCustomErrorHandler(handleError)(async () => {
    const numEditions = fieldNumEditions.validateBeforeSubmitting();
    const cip30Wallet =
      await modalTriggers.ModalConnectWallet.getCip30WalletOrOpenModal();
    await modalTriggers.ModalAsyncGenerator.openModal(() =>
      runFlow$PurchaseAsset({
        cip30Wallet,
        galleryArtwork,
        listingUtxo: entry.utxo,
        numEditions,
      })
    );
  });

  const handleDelist = withCustomErrorHandler(handleError)(async () => {
    const cip30Wallet =
      await modalTriggers.ModalConnectWallet.getCip30WalletOrOpenModal();
    await modalTriggers.ModalAsyncGenerator.openModal(() =>
      runFlow$DelistAsset({
        cip30Wallet,
        listingUtxo: entry.utxo,
      })
    );
  });

  return (
    <div className={cx(styles.container, className)} style={style}>
      <Flex.Col gap="20px">
        <Flex.Row gap="32px">
          <Definition
            label="Price"
            content={formatLovelaceAmount$Compact(
              parsedUtxo?.datumSecondary.editionPrice
            )}
            tooltip={formatLovelaceAmount$Standard(
              parsedUtxo?.datumSecondary.editionPrice
            )}
          />
          <Definition
            label="Available"
            content={formatInteger$Compact(parsedUtxo?.listedAsset.quantity)}
            tooltip={formatInteger$Standard(parsedUtxo?.listedAsset.quantity)}
          />
          <Definition
            label="Seller"
            content={
              <SellerNameViewer
                className={styles.link}
                entry={entry}
                unstyled
              />
            }
          />
        </Flex.Row>
        {isConnectedToSellerWallet ? (
          <Flex.Row gap="8px">
            <Flex.Cell flex="0 0 auto" asChild>
              <ButtonSecondary
                size="lg"
                icon={<MdRemoveShoppingCart />}
                content="Delist"
                disabled={busy}
                onClick={handleDelist}
              />
            </Flex.Cell>
          </Flex.Row>
        ) : (
          <Flex.Col gap="8px">
            <Flex.Row gap="8px">
              <Flex.Cell flex="0 0 auto" minWidth="200px" asChild>
                <InputNumEditions$Compact.UseField$Natural
                  api={fieldNumEditions}
                  disabled={busy}
                />
              </Flex.Cell>
              <Flex.Cell flex="1 1 auto" minWidth="0" asChild>
                <ButtonSecondary
                  size="lg"
                  icon={<MdShoppingCart />}
                  content="Purchase Now"
                  disabled={busy}
                  onClick={handlePurchase}
                />
              </Flex.Cell>
            </Flex.Row>
            {fieldNumEditions.error ? (
              <InlineErrorViewer content={fieldNumEditions.error.message} />
            ) : undefined}
          </Flex.Col>
        )}
      </Flex.Col>
    </div>
  );
}
